import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export default function ConsentPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recorded, setRecorded] = useState(false);

  useEffect(() => {
    api.get(`/consent/${token}`)
      .then((r) => {
        setInfo(r.data);
        if (r.data.consent?.given) setRecorded(true);
      })
      .catch(() => setError(true));
  }, [token]);

  const submit = async () => {
    if (!agreed) {
      toast.error("Please check the authorization box first.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/consent/${token}`, { agree: true });
      setRecorded(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream py-16 sm:py-24" data-testid="consent-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/" className="font-display text-2xl tracking-tight text-ink">ROAMIES<span className="text-flame">.</span></Link>

          {error && (
            <div className="mt-10 bg-white border border-black/10 p-8" data-testid="consent-error">
              <p className="font-display text-2xl uppercase">This authorization link is invalid or has expired.</p>
              <p className="mt-3 text-sm text-ink/60">Don't worry — your application is safe. Contact us and we'll send you a fresh link.</p>
            </div>
          )}

          {info && recorded && (
            <div className="mt-10 bg-white border border-black/10 p-8" data-testid="consent-recorded">
              <ShieldCheck className="w-8 h-8 text-flame" />
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mt-4">Screening Disclosure &amp; Authorization Record</p>
              <p className="font-display text-3xl uppercase mt-2">Consent recorded.</p>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div><p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">Traveler</p><p className="font-semibold mt-0.5" data-testid="consent-traveler-name">{info.first_name} {info.last_name}</p></div>
                <div><p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">Trip</p><p className="font-semibold mt-0.5">{info.trip_title}</p></div>
                <div><p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">Authorization status</p><p className="font-semibold mt-0.5">Given</p></div>
                <div><p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">Recorded (UTC)</p><p className="font-semibold mt-0.5" data-testid="consent-recorded-at">{info.consent?.at?.slice(0, 16).replace("T", " ")}</p></div>
                <div><p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">Disclosure version</p><p className="font-semibold mt-0.5">{info.consent?.disclosure_version}</p></div>
                <div><p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">Authorization version</p><p className="font-semibold mt-0.5">{info.consent?.authorization_version}</p></div>
                <div><p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">Receipt reference</p><p className="font-semibold mt-0.5" data-testid="consent-reference">{info.reference}</p></div>
              </div>
              <p className="mt-5 text-sm text-ink/70 leading-relaxed">
                If a screening is initiated, it will be handled by a legitimate third-party provider through their
                secure workflow — never by Roamies directly. Roamies receives only a status and reference.
                Submitting an application does not guarantee acceptance or a confirmed booking.
              </p>
            </div>
          )}

          {info && !recorded && (
            <div className="mt-10 bg-white border border-black/10 p-8" data-testid="consent-form-card">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame">Screening Disclosure & Authorization</p>
              <h1 className="font-display text-3xl sm:text-4xl uppercase mt-3 leading-tight">
                Hi {info.first_name} — one step before {info.trip_title}.
              </h1>

              <div className="mt-6 flex items-start gap-3 bg-flame/10 border border-flame/30 p-4" data-testid="consent-legal-notice">
                <AlertTriangle className="w-4 h-4 text-flame shrink-0 mt-0.5" />
                <p className="text-xs text-ink/70 leading-relaxed">
                  OWNER / LEGAL REVIEW REQUIRED — this wording is a draft placeholder and is not legally approved.
                </p>
              </div>

              <div className="mt-6 space-y-4 text-sm text-ink/75 leading-relaxed" data-testid="consent-text">
                <p>
                  Roamies may request an identity and background screening through a legitimate third-party
                  screening provider before confirming your spot. Roamies does not perform background searches itself.
                </p>
                <p>
                  If screening is initiated, you will complete it directly with the provider through their secure
                  workflow. Roamies receives only a status and reference — detailed reports stay inside the
                  provider's secure dashboard.
                </p>
                <p>
                  Any potential match is reviewed by a human before any decision is made. No adverse decision is
                  made solely from an unverified automated match.
                </p>
                <p className="text-xs text-ink/50">
                  Disclosure version: {info.disclosure_version} · Authorization version: {info.authorization_version}
                </p>
              </div>

              <label className="mt-6 flex items-start gap-3 text-sm font-semibold cursor-pointer" data-testid="consent-checkbox-label">
                <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} data-testid="consent-checkbox"
                  className="rounded-none border-ink/30 data-[state=checked]:bg-flame data-[state=checked]:border-flame mt-0.5" />
                I have read this disclosure and I authorize Roamies to request a screening from a third-party provider.
              </label>

              <Button onClick={submit} disabled={saving || !agreed} data-testid="consent-submit-btn"
                className="mt-6 w-full bg-flame hover:bg-flame/90 text-white rounded-none h-14 font-bold uppercase tracking-[0.2em] text-sm disabled:opacity-50">
                {saving ? "Recording..." : "I Authorize"}
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
